import { useContext, useEffect, useRef, useState } from "react";

import { directFileUrl, fileUrl, type Note, type Review } from "./api";
import { mountReader } from "./bookReader";
import type { Decision } from "./content";
import { ItemNotes, WhoContext, writerOf, type NoteEdits } from "./Notes";
import { AreaIcon, NoteIcon } from "./shell";

// RV-5 — the Long form room (books, reports): the Book Review Reader inside Launch OS. Pages exactly
// as the PDF looks (two at a time; one on a phone), read-along audio with the word lit on the page,
// Changes only and Next change, tap a word to play from there, select words to comment. Approve /
// Request changes for the chapter in view live in the thin player bar, not over the page.

type Props = {
  review: Review; decisions: Record<string, Decision>; notes: Note[];
  onDecide: (itemId: string, d: Decision) => void; onAskChanges: (itemId: string) => void;
  onComment: (c: { itemId: string; quote: string; anchor: string }) => void; onPlayNote: (n: Note) => void; noteEdits?: NoteEdits; onMoveNote?: (id: string, anchor: string, quote: string) => void; toast: (t: string) => void;
  canDecide?: boolean; // SH-3 — only the owner approves or asks for changes; teammates read and comment
};

export function LongRoom({ review, decisions, notes, onDecide, onAskChanges, onComment, onPlayNote, noteEdits, onMoveNote, toast, canDecide = true }: Props) {
  const root = useRef<HTMLDivElement>(null);
  const reader = useRef<ReturnType<typeof mountReader> | null>(null);
  const [chapter, setChapter] = useState<number | null>(null);
  const [drawer, setDrawer] = useState(false);
  const live = useRef({ onComment, toast, onMoveNote });
  live.current = { onComment, toast, onMoveNote };

  useEffect(() => {
    if (!root.current) return;
    const meta = review.meta as { session?: string };
    reader.current = mountReader(root.current, {
      reviewId: review.id,
      session: async () => (await fetch(await fileUrl(review.id, meta.session || "session.json"))).json(),
      fileUrl: (name: string) => fileUrl(review.id, name),
      directUrl: (name: string) => directFileUrl(review.id, name),
      onChapter: (c: number) => setChapter(c),
      onOpenNotes: () => setDrawer(true),
      onComment: (c: { quote: string; chapter: number; anchor: string }) => live.current.onComment({ itemId: `ch${c.chapter}`, quote: c.quote, anchor: c.anchor }),
      onMoveNote: (id: string, anchor: string, quote: string) => live.current.onMoveNote?.(id, anchor, quote),
      toast: (t: string) => live.current.toast(t),
    });
    return () => { reader.current?.destroy(); reader.current = null; };
  }, [review.id]);
  // SH-1 — each note's marker on the page wears its writer's color
  const who = useContext(WhoContext);
  useEffect(() => { reader.current?.setNotes(who ? notes.map((n) => ({ ...n, color: writerOf(who, n.by).color })) : notes); }, [notes, who]);
  useEffect(() => { root.current?.classList.toggle("drawer", drawer); }, [drawer]);

  // a tapped note: on a phone the notes cover the page, so close them first, then turn to the note
  const jump = (n: Note) => {
    if (root.current?.classList.contains("single")) setDrawer(false);
    reader.current?.goToNote(n.id).then((ok) => { if (!ok) toast("That note's place isn't on these pages."); });
  };
  const item = review.items.find((x) => x.id === `ch${chapter}`) || review.items[0];
  const d = item ? decisions[item.id] : undefined;

  return (
    // the class list is the reader's to manage (single / loading / boxing); React must never rewrite
    // it, or a redraw (opening notes, a new note) drops the phone layout
    <div className="lr-long" ref={root}>
      <div className="bk-mid">
        <div className="bk-stage">
          <button className="bk-flip l" type="button" aria-label="Previous pages">‹</button>
          <div className="bk-loading">Loading the chapter…</div>
          <button className="bk-flip r" type="button" aria-label="Next pages">›</button>
          <div className="bk-pgno" />
        </div>
        {drawer && (
          <aside className="bk-notes" aria-label="Your notes">
            <header><b>{who ? "Notes" : "Your notes"} · {notes.length}</b><button onClick={() => setDrawer(false)} aria-label="Close">✕</button></header>
            {notes.length ? review.items.map((it) => {
              const mine = notes.filter((n) => n.itemId === it.id);
              return mine.length ? <div key={it.id}><div className="ch">{it.title}</div><ItemNotes notes={mine} onPlay={onPlayNote} edits={noteEdits} onOpen={jump} /></div> : null;
            }) : <p className="empty">No notes yet. Select words on the page and tap Comment, or tap the comment button while it reads. Type or record a voice note.</p>}
          </aside>
        )}
      </div>
      <div className="bk-player">
        <div className="bk-row">
          <button className="ib bk-back" type="button" title="Back 10 seconds" aria-label="Back 10 seconds">↺ 10</button>
          <button className="ib play bk-play" type="button" aria-label="Play">▶</button>
          <button className="ib bk-fwd" type="button" title="Forward 10 seconds" aria-label="Forward 10 seconds">10 ↻</button>
          <select className="ib bk-rate" aria-label="Playback speed" />
          <button className="ib bk-next" type="button" title="Next change (N)"><span className="wide">Next change </span>⇥</button>
          <button className="ib bk-cm" type="button" title="Comment on what's being read" aria-label="Comment on what's being read"><NoteIcon /></button>
          <span className="bk-what">Loading…</span>
          {/* phone: the rest sits behind ⋯ (on desktop it shows inline); a long read is decided at the end */}
          <button className="ib bk-morebtn" type="button" aria-label="More: play mode, mark an area, notes, decide" onClick={() => root.current?.classList.toggle("more-open")}>⋯</button>
          <div className="bk-extra">
            <span className="bk-mode" role="group" aria-label="What to play">
              <button className="ib" type="button" data-m="changes" title="Play only the changed paragraphs, back to back">Changes</button>
              <button className="ib" type="button" data-m="all" title="Read the whole chapter, start to finish">Whole chapter</button>
            </span>
            <button className="ib bk-box" type="button" aria-pressed="false" title="Comment on a picture or diagram: drag a box around it" aria-label="Mark an area to comment on"><AreaIcon /><span className="wide"> Mark an area</span></button>
            {item && (
              <span className="bk-decide">
                <span className="lbl">{item.title}{d ? ` · ${d.decision === "approve" ? "Approved" : "Changes asked"}` : ""}</span>
                <button className="ib notes" type="button" onClick={() => { setDrawer((v) => !v); root.current?.classList.remove("more-open"); }} aria-pressed={drawer}>Notes {notes.length || ""}</button>
                {canDecide && <button className="ib chg" type="button" onClick={() => { root.current?.classList.remove("more-open"); onAskChanges(item.id); }}>Request changes</button>}
                {canDecide && <button className="ib ok" type="button" onClick={() => { root.current?.classList.remove("more-open"); onDecide(item.id, { decision: "approve" }); }}>✓ Approve</button>}
              </span>
            )}
          </div>
        </div>
        <div className="bk-scrub">
          <div className="bk-track" role="slider" tabIndex={0} aria-label="Position" aria-valuemin={0} aria-valuemax={100} aria-valuenow={0}><div className="rail" /><div className="bk-fill" /><div className="bk-segs" /><div className="bk-knob" /></div>
          <span className="bk-time">0:00 / 0:00</span>
        </div>
      </div>
      <button className="bk-cbtn" type="button" hidden>Comment on this</button>
      <audio className="bk-audio" preload="auto" playsInline />
    </div>
  );
}
